import {
  ExploreInsightCondition,
  ExploreInsightDirection,
} from '@api/api/explore-filter-schema';
import { Button } from '@design/components/button/Button';
import { Drawer } from '@design/components/drawer/Drawer';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { useDisableScroll } from '@helpers/hooks/use-disable-scroll/useDisableScroll';
import { DrawerContentWrapper } from '@terminal/components/drawer-content-wrapper/DrawerContentWrapper';
import { InsightDrawerAssetHeaderContent } from '@terminal/components/insight-drawer-header/InsightDrawerAssetHeaderContent';
import { useInsightsFullDrawer } from '@terminal/hooks/use-insights-full-drawer/useInsightsFullDrawer';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { EntityHeader } from '@terminal/modules/entity-header/EntityHeader';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useAssetOverviewNavigation } from '@terminal/widgets/asset-overview-widget/hooks/use-asset-overview-navigation/useAssetOverviewNavigation';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InsightsInfo } from './insights-info/InsightsInfo';
import { InsightsLeftSection } from './insights-left-section/InsightsLeftSection';
import styles from './ScenarioInsightsLegacyDrawer.module.scss';

export interface IconConfig {
  conditions: ExploreInsightCondition[];
  direction: ExploreInsightDirection;
  isCombined?: boolean;
  isMatched?: boolean;
  layout?: 'vertical' | 'horizontal';
}

interface AssetDrawerProps {
  entityTag: string;
}

export const ScenarioInsightsLegacyDrawer = ({
  entityTag,
}: AssetDrawerProps) => {
  const { params } = useQueryParams();
  const { t } = useTranslation(['widget']);
  const { closeDrawer } = useInsightsFullDrawer();
  const isInsightDrawerHeaderEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-4942-insight-drawer-header')
  );
  const { data, isLoading } = useEntity(entityTag);
  const entity = data?.data;
  const { navigateToAssetOverview } = useAssetOverviewNavigation(
    data?.data,
    POSTHOG_TRACKING_SOURCES.SCENARIO_INSIGHT_DRAWER
  );
  useDisableScroll();

  const [iconConfig, setIconConfig] = useState<IconConfig>();

  return (
    <div data-testid="insights-drawer">
      <Drawer
        className={styles.styledDrawer}
        isOpen
        handleCloseClick={closeDrawer}
        headerButton={
          entity && (
            <Button
              iconName="Expand"
              label={t('widget:assetOverview.assetOverview')}
              onClick={data?.data && navigateToAssetOverview}
              variant="empty"
              size="xsmall"
              data-testid="open-asset-overview"
            />
          )
        }
      >
        <DrawerContentWrapper>
          {isLoading ? (
            <SkeletonLoader
              data-testid="insight-header-loading"
              areas={[
                {
                  styles: {
                    width: '100%',
                    height: 40,
                  },
                },
              ]}
            />
          ) : (
            <>
              {entity &&
                (isInsightDrawerHeaderEnabled ? (
                  <div className={styles.styledEntityHeader}>
                    <InsightDrawerAssetHeaderContent
                      entity={entity}
                      source={POSTHOG_TRACKING_SOURCES.SCENARIO_INSIGHT_DRAWER}
                    />
                  </div>
                ) : (
                  <EntityHeader
                    className={styles.styledEntityHeader}
                    entity={entity}
                    isDrawer
                    headerButton={<></>}
                  />
                ))}
            </>
          )}
          <div className={styles.sectionWrapper}>
            <div className={styles.section}>
              {entity && (
                <InsightsLeftSection
                  entity={entity}
                  activeInsightId={params.activeInsightId ?? undefined}
                  handleSetIconConfig={setIconConfig}
                />
              )}
            </div>
            <div className={styles.section}>
              <InsightsInfo
                insightId={params.activeInsightId}
                iconConfig={iconConfig}
              />
            </div>
          </div>
        </DrawerContentWrapper>
      </Drawer>
    </div>
  );
};
